import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSpinner,
  FaTrash,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { yardVehicleService } from '../../../../services/yardVehicleService';
import { AVAILABILITY_TIERS, tierLabel, tierSlaLabel } from '../../../../utils/availabilityTiers';
import { GRADE_META, LISTABLE_GRADES } from '../../../../utils/usedPartGrades';
import { formatIndianRegistration } from '../../../../utils/indianRegistration';

function formatInr(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
}

function partHasPrice(part) {
  return part.price !== '' && part.price != null;
}

function PriceHistoryChart({ data }) {
  if (!data?.length) return null;
  const prices = data.map((d) => Number(d.price)).filter((n) => Number.isFinite(n));
  if (!prices.length) return null;

  const w = 280;
  const h = 96;
  const pad = 8;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;

  const points = prices
    .map((price, i) => {
      const x = pad + (i / Math.max(prices.length - 1, 1)) * (w - pad * 2);
      const y = pad + (1 - (price - min) / range) * (h - pad * 2);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke="#059669"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
      />
    </svg>
  );
}

function PriceConfidence({ state }) {
  if (!state) return null;
  if (state.error) {
    return (
      <span className="text-xs text-red-600 flex items-center gap-1">
        <FaExclamationTriangle className="shrink-0" />
        {state.error}
      </span>
    );
  }
  if (state.warning) {
    return (
      <span className="text-xs text-amber-700 flex items-center gap-1">
        <FaExclamationTriangle className="shrink-0" />
        Above market — confirm to publish
      </span>
    );
  }
  if (state.confidence === 'fair') {
    return (
      <span className="text-xs text-emerald-700 flex items-center gap-1 font-medium">
        <FaCheckCircle className="shrink-0" />
        Fair price
      </span>
    );
  }
  return null;
}

function AvailabilityTierRadios({ part, onChange, compact = false }) {
  const priced = partHasPrice(part);
  const missingTier = priced && !part.availabilityTier;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-semibold text-slate-600">Availability tier</p>
        {priced && (
          <span className="text-[10px] text-slate-400">Required for priced parts</span>
        )}
        {missingTier && (
          <span className="text-[10px] text-red-600 font-medium">Select a tier to publish</span>
        )}
      </div>
      <div
        className={`grid gap-2 ${compact ? 'sm:grid-cols-1' : 'sm:grid-cols-3'}`}
        role="radiogroup"
        aria-label="Availability tier"
      >
        {AVAILABILITY_TIERS.map((t) => {
          const selected = part.availabilityTier === t.value;
          return (
            <label
              key={t.value}
              title={t.slaLabel}
              className={`relative text-left rounded-lg border p-2.5 text-xs cursor-pointer transition-colors ${
                selected
                  ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500'
                  : missingTier
                    ? 'border-slate-200 bg-white hover:border-emerald-300'
                    : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <input
                type="radio"
                name={`tier-${part.suggestionId}`}
                value={t.value}
                checked={selected}
                onChange={() => onChange(part.suggestionId, { availabilityTier: t.value })}
                className="sr-only"
              />
              <span className="font-semibold text-slate-800 block">{t.label}</span>
              <span className="text-slate-500 block mt-0.5">{t.desc}</span>
              <span className="text-emerald-700 text-[10px] font-medium mt-1 inline-flex items-center gap-1">
                <FaInfoCircle className="shrink-0" />
                {t.slaLabel}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function HighPriceModal({ open, partName, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-slate-900">Price above market</h3>
        <p className="mt-2 text-sm text-slate-600">
          <strong>{partName}</strong> is priced significantly above the market ceiling. Are you sure
          you want to publish at this price?
        </p>
        <div className="mt-6 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700"
          >
            Yes, publish anyway
          </button>
        </div>
      </div>
    </div>
  );
}

function DuplicateBlockModal({ open, existingVehicleId, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-slate-900">Duplicate vehicle</h3>
        <p className="mt-2 text-sm text-slate-600">
          This vehicle is already in your active listings.
        </p>
        {existingVehicleId && (
          <Link
            to={`/vendor/dashboard/yard/${existingVehicleId}/parts`}
            className="mt-4 inline-flex text-sm font-semibold text-emerald-700 hover:text-emerald-900"
          >
            View existing listing →
          </Link>
        )}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function PublishSummaryPanel({
  parts,
  vehicle,
  publishableCount,
  draftCount,
  onChange,
  onBack,
  onConfirm,
  publishing,
  adminReviewWarning,
}) {
  const publishableParts = parts.filter((p) => !p.removed && partHasPrice(p) && p.availabilityTier);

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-800 mb-2"
        >
          ← Back to pricing
        </button>
        <h2 className="text-xl font-bold text-slate-900">Publish summary</h2>
        <p className="text-sm text-slate-500 mt-1">
          Review each part before publishing to the catalog. Parts without price or tier stay as
          drafts.
        </p>
      </div>

      {adminReviewWarning && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
          <FaExclamationTriangle className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Admin review required</p>
            <p className="mt-1">{adminReviewWarning}</p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Part</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Price</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Grade</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 min-w-[200px]">
                  Tier
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {publishableParts.map((part) => (
                <tr key={part.suggestionId} className="align-top">
                  <td className="px-4 py-3 font-medium text-slate-900">{part.partCategory}</td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min="0"
                      value={part.price ?? ''}
                      onChange={(e) => onChange(part.suggestionId, { price: e.target.value })}
                      className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={part.usedGrade || 'B'}
                      onChange={(e) => onChange(part.suggestionId, { usedGrade: e.target.value })}
                      className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    >
                      {LISTABLE_GRADES.map((g) => (
                        <option key={g} value={g}>
                          {g} · {GRADE_META[g].label.replace('Used — ', '')}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={part.availabilityTier || ''}
                      onChange={(e) =>
                        onChange(part.suggestionId, { availabilityTier: e.target.value })
                      }
                      title={tierSlaLabel(part.availabilityTier)}
                      className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    >
                      <option value="" disabled>
                        Select tier
                      </option>
                      {AVAILABILITY_TIERS.map((t) => (
                        <option key={t.value} value={t.value} title={t.slaLabel}>
                          {t.label} ({t.slaHours}h SLA)
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {publishableParts.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-slate-500">
            No parts ready to publish. Add prices and availability tiers first.
          </p>
        )}
      </div>

      {draftCount > 0 && (
        <p className="text-xs text-slate-500">
          {draftCount} part{draftCount !== 1 ? 's' : ''} without price or tier will be saved as
          drafts.
        </p>
      )}

      <div className="sticky bottom-4 bg-white/90 backdrop-blur border border-slate-200 rounded-xl p-4 shadow-lg flex flex-wrap gap-3 items-center">
        <p className="text-sm text-slate-600 flex-1 min-w-[200px]">
          {vehicle?.make} {vehicle?.model} ({vehicle?.year}) ·{' '}
          {formatIndianRegistration(vehicle?.registrationNumber || '')}
        </p>
        <button
          type="button"
          onClick={onConfirm}
          disabled={publishing || publishableCount === 0}
          className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
        >
          {publishing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
          Publish {publishableCount} part{publishableCount !== 1 ? 's' : ''}
        </button>
      </div>
    </div>
  );
}

function PublishConfirmation({ result, vehicle, onDone }) {
  const v = result?.vehicle || vehicle;
  return (
    <div className="max-w-lg mx-auto text-center space-y-6 py-8">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600">
        <FaCheckCircle className="text-3xl" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Parts published</h2>
        <p className="text-slate-600 mt-2">
          <strong>{result.published}</strong> part{result.published !== 1 ? 's' : ''} are now live in
          the catalog.
          {result.draftCount > 0 && (
            <>
              {' '}
              {result.draftCount} saved as draft{result.draftCount !== 1 ? 's' : ''}.
            </>
          )}
        </p>
      </div>

      {result.adminReviewRequired && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 text-left">
          This vehicle is flagged for admin review (duplicate registration in your city).
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 text-left flex gap-4">
        {v?.photoUrl && (
          <img
            src={v.photoUrl}
            alt=""
            className="w-20 h-20 rounded-lg object-cover shrink-0"
          />
        )}
        <div>
          <p className="font-bold text-slate-900">
            {v?.make} {v?.model} ({v?.year})
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {formatIndianRegistration(v?.registrationNumber || '')}
          </p>
          <p className="text-xs text-emerald-700 font-medium mt-2">
            {result.published} live listing{result.published !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onDone}
        className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
      >
        Back to yard vehicles
      </button>
    </div>
  );
}

function PartPricingRow({ part, vehicleId, expanded, onToggle, onChange, onRemove }) {
  const checkTimer = useRef(null);
  const [checking, setChecking] = useState(false);

  const runPriceCheck = useCallback(
    async (price, confirmHighPrice = false) => {
      if (part.removed) return;
      if (price === '' || price === null || price === undefined) {
        onChange(part.suggestionId, { priceState: null, priceError: null });
        return;
      }
      setChecking(true);
      try {
        await yardVehicleService.priceCheck(vehicleId, {
          partCategory: part.partCategory,
          price: Number(price),
          marketFloor: part.market?.marketFloor,
          marketCeiling: part.market?.marketCeiling,
          noMarketData: part.noMarketData,
          confirmHighPrice,
          dryRun: true,
        });
        onChange(part.suggestionId, {
          priceState: { confidence: 'fair', mode: 'FIXED' },
          priceError: null,
          needsHighPriceConfirm: false,
        });
      } catch (err) {
        const data = err?.response?.data;
        if (data?.hardBlock) {
          onChange(part.suggestionId, {
            priceState: { error: data.message },
            priceError: data.message,
            needsHighPriceConfirm: false,
          });
        } else if (data?.requiresConfirmation) {
          onChange(part.suggestionId, {
            priceState: { warning: true },
            needsHighPriceConfirm: true,
            priceError: null,
          });
        } else {
          onChange(part.suggestionId, { priceError: data?.message || 'Price check failed' });
        }
      } finally {
        setChecking(false);
      }
    },
    [part, vehicleId, onChange]
  );

  const handlePriceChange = (value) => {
    onChange(part.suggestionId, { price: value, confirmHighPrice: false });
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(() => runPriceCheck(value), 500);
  };

  const chartData = useMemo(() => {
    const hist = part.market?.priceHistory30d || [];
    return hist.map((p) => ({
      label: new Date(p.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
      price: p.price,
    }));
  }, [part.market?.priceHistory30d]);

  if (part.removed) return null;

  const priced = partHasPrice(part);
  const missingTier = priced && !part.availabilityTier;

  return (
    <div
      className={`rounded-xl border bg-white overflow-hidden ${
        missingTier ? 'border-amber-300' : 'border-slate-200'
      }`}
    >
      <div
        className="flex flex-wrap items-center gap-3 p-4 cursor-pointer hover:bg-slate-50/80"
        onClick={onToggle}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        role="button"
        tabIndex={0}
      >
        <div className="flex-1 min-w-[140px]">
          <p className="font-semibold text-slate-900 text-sm">{part.partCategory}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Suggested {formatInr(part.suggestedPriceMin)} – {formatInr(part.suggestedPriceMax)}
            {part.demandIndex != null && (
              <span className="ml-2 text-emerald-600">Demand {part.demandIndex}/100</span>
            )}
          </p>
          {priced && part.availabilityTier && (
            <p className="text-[10px] text-emerald-700 mt-1" title={tierSlaLabel(part.availabilityTier)}>
              {tierLabel(part.availabilityTier)} · {tierSlaLabel(part.availabilityTier)}
            </p>
          )}
        </div>
        <div className="w-full sm:w-36" onClick={(e) => e.stopPropagation()}>
          <input
            type="number"
            min="0"
            placeholder="Price (₹)"
            value={part.price ?? ''}
            onChange={(e) => handlePriceChange(e.target.value)}
            className={`w-full rounded-lg border px-3 py-2 text-sm ${
              part.priceError ? 'border-red-400 bg-red-50' : 'border-slate-300'
            }`}
          />
        </div>
        <div className="flex items-center gap-2 min-w-[120px]">
          {checking ? (
            <FaSpinner className="animate-spin text-slate-400" />
          ) : (
            <PriceConfidence state={part.priceState} />
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(part.suggestionId);
          }}
          className="p-2 text-slate-400 hover:text-red-600"
          title="Remove suggestion"
        >
          <FaTrash className="text-sm" />
        </button>
        {expanded ? (
          <FaChevronUp className="text-slate-400" />
        ) : (
          <FaChevronDown className="text-slate-400" />
        )}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-4">
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Grade (seller-declared)</p>
            <div className="flex flex-wrap gap-2">
              {LISTABLE_GRADES.map((g) => {
                const meta = GRADE_META[g];
                const on = part.usedGrade === g;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => onChange(part.suggestionId, { usedGrade: g })}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold border ${
                      on
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-600 border-slate-300'
                    }`}
                  >
                    {g} · {meta.label.replace('Used — ', '')}
                  </button>
                );
              })}
            </div>
          </div>

          <AvailabilityTierRadios part={part} onChange={onChange} />

          {chartData.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Market price (30 days)</p>
              <div className="h-32 w-full bg-white rounded-lg border border-slate-200 p-2">
                <PriceHistoryChart data={chartData} />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                {formatInr(chartData[0]?.price)} → {formatInr(chartData[chartData.length - 1]?.price)}{' '}
                (30d)
              </p>
              {part.noMarketData && (
                <p className="text-[10px] text-amber-700 mt-1">
                  No external market cell — guardrails skipped
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {priced && !expanded && (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/30">
          <AvailabilityTierRadios part={part} onChange={onChange} compact />
        </div>
      )}
    </div>
  );
}

export default function YardPartsPricing() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [parts, setParts] = useState([]);
  const [city, setCity] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [highPriceModal, setHighPriceModal] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const [step, setStep] = useState('pricing');
  const [publishResult, setPublishResult] = useState(null);
  const [adminReviewWarning, setAdminReviewWarning] = useState(null);

  const loadSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await yardVehicleService.suggestParts(vehicleId);
      setVehicle(data.vehicle);
      setCity(data.city);
      setParts(
        (data.parts || []).map((p) => ({
          ...p,
          price: '',
          usedGrade: 'B',
          availabilityTier: null,
          removed: false,
          confirmHighPrice: false,
          priceState: null,
          priceError: null,
          needsHighPriceConfirm: false,
        }))
      );
    } catch {
      toast.error('Failed to load part suggestions');
      navigate('/vendor/dashboard/yard');
    } finally {
      setLoading(false);
    }
  }, [vehicleId, navigate]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const updatePart = (suggestionId, patch) => {
    setParts((prev) =>
      prev.map((p) => (p.suggestionId === suggestionId ? { ...p, ...patch } : p))
    );
  };

  const removePart = (suggestionId) => {
    updatePart(suggestionId, { removed: true });
  };

  const activeParts = parts.filter((p) => !p.removed);
  const publishableParts = activeParts.filter((p) => partHasPrice(p) && p.availabilityTier);
  const draftCount = activeParts.filter(
    (p) => !partHasPrice(p) || !p.availabilityTier
  ).length;
  const pricedWithoutTier = activeParts.filter((p) => partHasPrice(p) && !p.availabilityTier);
  const hasBlockErrors = activeParts.some((p) => p.priceError);

  const buildPublishPayload = (confirmIds = new Set()) =>
    activeParts.map((p) => ({
      partCategory: p.partCategory,
      subSystem: p.subSystem,
      price: p.price === '' ? null : p.price,
      usedGrade: p.usedGrade,
      availabilityTier: p.availabilityTier,
      removed: false,
      confirmHighPrice: confirmIds.has(p.suggestionId) || p.confirmHighPrice,
      market: p.market,
    }));

  const doPublish = async (confirmIds = new Set()) => {
    setPublishing(true);
    try {
      const result = await yardVehicleService.publishParts(vehicleId, {
        parts: buildPublishPayload(confirmIds),
      });
      setPublishResult(result);
      setStep('confirmation');
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === 'DUPLICATE_ACTIVE_LISTING') {
        setDuplicateModal({ existingVehicleId: data.existingVehicleId });
        return;
      }
      if (data?.warnings?.length) {
        const first = activeParts.find((p) => p.partCategory === data.warnings[0].partCategory);
        setHighPriceModal({
          suggestionId: first?.suggestionId,
          partName: data.warnings[0].partCategory,
        });
      } else {
        toast.error(data?.message || 'Publish failed');
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleOpenSummary = async () => {
    if (hasBlockErrors) {
      toast.error('Fix blocked prices before publishing');
      return;
    }
    if (pricedWithoutTier.length) {
      toast.error('Select an availability tier for every priced part');
      return;
    }
    if (publishableParts.length === 0) {
      toast.error('Add at least one part with price and availability tier');
      return;
    }
    const needsConfirm = publishableParts.filter((p) => p.needsHighPriceConfirm);
    if (needsConfirm.length) {
      setHighPriceModal({
        suggestionId: needsConfirm[0].suggestionId,
        partName: needsConfirm[0].partCategory,
      });
      return;
    }

    try {
      const check = await yardVehicleService.publishCheck(vehicleId);
      if (check.blocked) {
        setDuplicateModal({ existingVehicleId: check.existingVehicleId });
        return;
      }
      setAdminReviewWarning(check.adminReviewRequired ? check.message : null);
      setStep('summary');
    } catch {
      toast.error('Could not verify vehicle registration');
    }
  };

  const handleConfirmPublish = async () => {
    if (publishableParts.some((p) => p.needsHighPriceConfirm)) {
      const first = publishableParts.find((p) => p.needsHighPriceConfirm);
      setHighPriceModal({
        suggestionId: first.suggestionId,
        partName: first.partCategory,
      });
      return;
    }
    await doPublish();
  };

  const confirmHighPrice = () => {
    if (!highPriceModal) return;
    const ids = new Set([highPriceModal.suggestionId]);
    updatePart(highPriceModal.suggestionId, { confirmHighPrice: true });
    setHighPriceModal(null);
    if (step === 'summary') {
      doPublish(ids);
    } else {
      setStep('summary');
      setAdminReviewWarning(null);
      yardVehicleService.publishCheck(vehicleId).then((check) => {
        if (check.blocked) {
          setDuplicateModal({ existingVehicleId: check.existingVehicleId });
          setStep('pricing');
          return;
        }
        setAdminReviewWarning(check.adminReviewRequired ? check.message : null);
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <FaSpinner className="animate-spin mr-2" />
        Generating part suggestions…
      </div>
    );
  }

  if (step === 'confirmation' && publishResult) {
    return (
      <PublishConfirmation
        result={publishResult}
        vehicle={vehicle}
        onDone={() => navigate('/vendor/dashboard/yard')}
      />
    );
  }

  if (step === 'summary') {
    return (
      <div>
        <HighPriceModal
          open={Boolean(highPriceModal)}
          partName={highPriceModal?.partName}
          onConfirm={confirmHighPrice}
          onCancel={() => setHighPriceModal(null)}
        />
        <DuplicateBlockModal
          open={Boolean(duplicateModal)}
          existingVehicleId={duplicateModal?.existingVehicleId}
          onClose={() => setDuplicateModal(null)}
        />
        <PublishSummaryPanel
          parts={parts}
          vehicle={vehicle}
          publishableCount={publishableParts.length}
          draftCount={draftCount}
          onChange={updatePart}
          onBack={() => setStep('pricing')}
          onConfirm={handleConfirmPublish}
          publishing={publishing}
          adminReviewWarning={adminReviewWarning}
        />
      </div>
    );
  }

  return (
    <div>
      <HighPriceModal
        open={Boolean(highPriceModal)}
        partName={highPriceModal?.partName}
        onConfirm={confirmHighPrice}
        onCancel={() => setHighPriceModal(null)}
      />
      <DuplicateBlockModal
        open={Boolean(duplicateModal)}
        existingVehicleId={duplicateModal?.existingVehicleId}
        onClose={() => setDuplicateModal(null)}
      />

      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/vendor/dashboard/yard')}
          className="text-sm text-slate-500 hover:text-slate-800 mb-2"
        >
          ← Back to yard vehicles
        </button>
        <h2 className="text-xl font-bold text-slate-900">
          Price parts — {vehicle?.make} {vehicle?.model} ({vehicle?.year})
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {city} demand data · {activeParts.length} high-value parts suggested
        </p>
        <p className="text-sm font-semibold text-emerald-700 mt-2">
          {publishableParts.length} ready to publish · {draftCount} draft
          {draftCount !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="space-y-3 mb-8">
        {activeParts.map((part) => (
          <PartPricingRow
            key={part.suggestionId}
            part={part}
            vehicleId={vehicleId}
            expanded={expandedId === part.suggestionId}
            onToggle={() =>
              setExpandedId((id) => (id === part.suggestionId ? null : part.suggestionId))
            }
            onChange={updatePart}
            onRemove={removePart}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-3 sticky bottom-4 bg-white/90 backdrop-blur border border-slate-200 rounded-xl p-4 shadow-lg">
        <p className="text-xs text-slate-500 flex-1 min-w-[200px] self-center">
          Priced parts need an availability tier. Gate C-F2 runs on each price entry.
        </p>
        <button
          type="button"
          onClick={handleOpenSummary}
          disabled={publishing || hasBlockErrors || publishableParts.length === 0}
          className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
        >
          Review & publish ({publishableParts.length})
        </button>
      </div>
    </div>
  );
}
